import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Heart } from 'lucide-react';

/**
 * 💜 Day One Hook - Single emotional moment on first day
 * 
 * "I'll remember what matters to you — you don't have to."
 * 
 * Appears once on Day 1 of trial. Not intrusive, just memorable.
 * This single sentence increases retention more than any feature.
 */
const DayOneHook = () => {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    if (!user) return;
    
    // Skip DayOneHook for admin users
    if (user.is_admin || user.role === 'admin') {
      return;
    }

    // Check if user is on Day 1 of their journey
    const checkDayOne = async () => {
      try {
        // Get user's trial start from subscription
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/user/subscription`,
          { credentials: 'include' }
        );

        if (response.ok) {
          const data = await response.json();
          const subscription = data.subscription || {};
          const trialStart = subscription.trial_start || subscription.created_at;

          if (trialStart) {
            const startDate = new Date(trialStart);
            const now = new Date();
            const hoursElapsed = (now - startDate) / (1000 * 60 * 60);

            // Show only within first 24 hours
            const isDayOne = hoursElapsed <= 24;

            // Check if already shown this session
            const hookKey = `lifeflo_day1_hook_${user.id}`;
            const alreadyShown = sessionStorage.getItem(hookKey);

            if (isDayOne && !alreadyShown) {
              // Small delay to let the page settle
              setTimeout(() => {
                setShow(true);
                sessionStorage.setItem(hookKey, 'shown');
              }, 2000);
            }
          }
        }
      } catch (error) {
        console.error('Day one hook check failed:', error);
      }
    };

    checkDayOne();
  }, [user]);

  const handleDismiss = (e) => {
    if (e) e.preventDefault();
    setFadeOut(true);
    setTimeout(() => setShow(false), 500);
  };

  if (!show) return null;

  return (
    <div 
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-6 transition-all duration-500 ${
        fadeOut ? 'opacity-0' : 'opacity-100'
      }`}
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(8px)' }}
      onClick={handleDismiss}
    >
      <div 
        className={`max-w-sm w-full transform transition-all duration-500 ${
          fadeOut ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* The Hook Card */}
        <div className="bg-white rounded-3xl p-8 shadow-2xl text-center relative">
          
          {/* Gentle heart pulse */}
          <div className="mb-6">
            <div className="w-16 h-16 mx-auto bg-gradient-to-br from-teal-500/20 to-cyan-500/20 rounded-full flex items-center justify-center animate-pulse">
              <Heart className="w-8 h-8 text-teal-500" fill="currentColor" />
            </div>
          </div>

          {/* The One Sentence */}
          <p className="text-xl font-medium text-gray-800 leading-relaxed mb-2">
            I'll remember what matters to you
          </p>
          <p className="text-xl text-teal-600 font-medium">
            — you don't have to.
          </p>

          {/* Subtle signature */}
          <p className="text-sm text-gray-400 mt-6 mb-6">
            — LifeFlo
          </p>

          {/* Clear Continue button */}
          <button
            onClick={handleDismiss}
            className="w-full py-4 px-6 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-2xl font-semibold text-lg hover:from-teal-600 hover:to-cyan-600 transition-all shadow-lg active:scale-95"
          >
            Continue →
          </button>
        </div>
      </div>
    </div>
  );
};

export default DayOneHook;
